import {
  clonePoint,
  closestPointOnSegment,
  distanceSquared,
  movePoint,
  quadraticBezierPoint,
} from './document.ts';
import { DEFAULT_CORNER_RADIUS, MIN_RING_VERTICES, type Ring, type Vec2 } from './types.ts';

const CORNER_EPSILON = 0.01;
const DEFAULT_CORNER_SEGMENTS = 8;
const DEFAULT_EDGE_SEGMENTS = 10;

export function createRing(vertices: readonly Vec2[], cornerRadius = DEFAULT_CORNER_RADIUS): Ring {
  return {
    vertices: vertices.map(clonePoint),
    edges: vertices.map(() => ({ bend: null })),
    cornerRadius,
  };
}

export function cloneRing(ring: Ring): Ring {
  return {
    vertices: ring.vertices.map(clonePoint),
    edges: ring.edges.map((edge) => ({ bend: edge.bend ? clonePoint(edge.bend) : null })),
    cornerRadius: ring.cornerRadius,
  };
}

type Shoulder = { in: Vec2; out: Vec2; radius: number };

function computeShoulders(ring: Ring): Shoulder[] {
  const n = ring.vertices.length;

  return ring.vertices.map((vertex, i) => {
    const prev = ring.vertices[(i - 1 + n) % n];
    const next = ring.vertices[(i + 1) % n];

    if (!prev || !next) {
      return { in: clonePoint(vertex), out: clonePoint(vertex), radius: 0 };
    }

    const lenIn = Math.hypot(prev.x - vertex.x, prev.y - vertex.y);
    const lenOut = Math.hypot(next.x - vertex.x, next.y - vertex.y);
    const radius = Math.max(0, Math.min(ring.cornerRadius, lenIn / 2, lenOut / 2));

    return {
      in: movePoint(vertex, prev, radius),
      out: movePoint(vertex, next, radius),
      radius,
    };
  });
}

type OutlineCacheEntry = {
  cornerSegments: number;
  edgeSegments: number;
  signature: Float64Array;
  points: Vec2[];
};

const outlineCache = new WeakMap<Ring, OutlineCacheEntry>();

let outlineCalls = 0;
let outlineBuilds = 0;

/**
 * Counters behind the hover budget guard in `tests/unit/hoverBudget.test.ts`.
 *
 * `builds` is outlines actually tessellated, so it catches the cache being dropped. `calls` is
 * requests, so it catches a caller that re-derives geometry per rim sample or per path step
 * instead of once up front — the shape of the regression that made a single hover cost two to
 * three frames. Both are plain counters; nothing reads them in production.
 */
export function ringOutlineStats(): { calls: number; builds: number } {
  return { calls: outlineCalls, builds: outlineBuilds };
}

/**
 * Everything `buildRingOutline` reads, flattened, so a cached outline can be proven current.
 *
 * Caching on ring identity alone would be wrong: rings are edited in place — `moveRingVertex`,
 * `moveBendHandle`, `setCornerRadius` and the group translate in `transform.ts` all mutate the
 * existing objects — so a dragged vertex would keep hit-testing against its old outline. Comparing
 * the inputs is O(vertices) against tessellation's O(vertices x segments) of Bezier and hypot work,
 * and unlike a hash it cannot collide.
 */
function signatureLength(ring: Ring): number {
  let length = 2 + ring.vertices.length * 2;

  for (const edge of ring.edges) {
    length += edge.bend ? 3 : 1;
  }

  return length;
}

export function captureRingSignature(ring: Ring): Float64Array {
  return writeSignature(ring);
}

export function ringSignatureMatches(ring: Ring, signature: Float64Array): boolean {
  return signatureMatches(ring, signature);
}

function writeSignature(ring: Ring): Float64Array {
  const signature = new Float64Array(signatureLength(ring));
  let i = 0;

  signature[i++] = ring.cornerRadius;
  signature[i++] = ring.vertices.length;

  for (const vertex of ring.vertices) {
    signature[i++] = vertex.x;
    signature[i++] = vertex.y;
  }

  for (const edge of ring.edges) {
    if (edge.bend) {
      signature[i++] = 1;
      signature[i++] = edge.bend.x;
      signature[i++] = edge.bend.y;
    } else {
      signature[i++] = 0;
    }
  }

  return signature;
}

/**
 * Compares without building a signature first. The check runs on every request — several hundred
 * times per hover on a busy map — so allocating a throwaway array here would cost more than the
 * comparison it enables.
 */
function signatureMatches(ring: Ring, signature: Float64Array): boolean {
  if (signature.length !== signatureLength(ring)) {
    return false;
  }

  let i = 0;

  if (signature[i++] !== ring.cornerRadius || signature[i++] !== ring.vertices.length) {
    return false;
  }

  for (const vertex of ring.vertices) {
    if (signature[i++] !== vertex.x || signature[i++] !== vertex.y) {
      return false;
    }
  }

  for (const edge of ring.edges) {
    const bend = edge.bend;

    if (!bend) {
      if (signature[i++] !== 0) {
        return false;
      }

      continue;
    }

    if (signature[i++] !== 1 || signature[i++] !== bend.x || signature[i++] !== bend.y) {
      return false;
    }
  }

  return true;
}

/**
 * Tessellates a ring into a smooth closed polyline: each corner is filleted with a quadratic
 * Bezier through the raw vertex, and each edge is either straight or, if it has a `bend` control
 * point, a quadratic Bezier through that point. Islands, shallow-water zones, and the start area
 * all render and hit-test against this outline.
 *
 * The result is cached per ring and reused until the ring's geometry actually changes, because
 * collision, the planning and prediction circles, and every map redraw all re-derive the same
 * outlines many times per frame. **The returned array is shared — treat it as read-only.**
 */
/**
 * Island-style corner fillets on an open polyline. Endpoints stay put; interior vertices are
 * rounded with the same quadratic Bezier as `tessellateRing` (radius clamped to half the shorter
 * adjacent edge). Used by editor routes so Enter matches closing an island.
 */
export function tessellateOpenPath(
  points: readonly Vec2[],
  cornerRadius = DEFAULT_CORNER_RADIUS,
  cornerSegments = DEFAULT_CORNER_SEGMENTS,
): Vec2[] {
  const n = points.length;

  if (n < 2 || cornerRadius <= CORNER_EPSILON) {
    return points.map(clonePoint);
  }

  const outline: Vec2[] = [];
  const first = points[0];

  if (first) {
    outline.push(clonePoint(first));
  }

  for (let i = 1; i < n - 1; i += 1) {
    const prev = points[i - 1];
    const vertex = points[i];
    const next = points[i + 1];

    if (!prev || !vertex || !next) {
      continue;
    }

    const lenIn = Math.hypot(prev.x - vertex.x, prev.y - vertex.y);
    const lenOut = Math.hypot(next.x - vertex.x, next.y - vertex.y);
    const radius = Math.max(0, Math.min(cornerRadius, lenIn / 2, lenOut / 2));

    if (radius <= CORNER_EPSILON) {
      outline.push(clonePoint(vertex));
      continue;
    }

    const shoulderIn = movePoint(vertex, prev, radius);
    const shoulderOut = movePoint(vertex, next, radius);

    for (let s = 0; s < cornerSegments; s += 1) {
      outline.push(quadraticBezierPoint(shoulderIn, vertex, shoulderOut, s / cornerSegments));
    }

    outline.push(clonePoint(shoulderOut));
  }

  const last = points[n - 1];

  if (last) {
    outline.push(clonePoint(last));
  }

  return outline;
}

export function tessellateRing(
  ring: Ring,
  cornerSegments = DEFAULT_CORNER_SEGMENTS,
  edgeSegments = DEFAULT_EDGE_SEGMENTS,
): Vec2[] {
  outlineCalls += 1;

  const cached = outlineCache.get(ring);

  if (
    cached &&
    cached.cornerSegments === cornerSegments &&
    cached.edgeSegments === edgeSegments &&
    signatureMatches(ring, cached.signature)
  ) {
    return cached.points;
  }

  outlineBuilds += 1;
  const points = buildRingOutline(ring, cornerSegments, edgeSegments);
  outlineCache.set(ring, {
    cornerSegments,
    edgeSegments,
    signature: writeSignature(ring),
    points,
  });

  return points;
}

function buildRingOutline(
  ring: Ring,
  cornerSegments: number,
  edgeSegments: number,
): Vec2[] {
  const n = ring.vertices.length;

  if (n < MIN_RING_VERTICES) {
    return ring.vertices.map(clonePoint);
  }

  const shoulders = computeShoulders(ring);
  const points: Vec2[] = [];

  for (let i = 0; i < n; i += 1) {
    const vertex = ring.vertices[i];
    const shoulder = shoulders[i];

    if (!vertex || !shoulder) {
      continue;
    }

    if (shoulder.radius > CORNER_EPSILON) {
      for (let s = 0; s < cornerSegments; s += 1) {
        points.push(quadraticBezierPoint(shoulder.in, vertex, shoulder.out, s / cornerSegments));
      }

      points.push(clonePoint(shoulder.out));
    } else {
      points.push(clonePoint(vertex));
    }

    const edge = ring.edges[i];
    const nextShoulder = shoulders[(i + 1) % n];
    const bend = edge?.bend ?? null;

    if (bend && nextShoulder) {
      for (let s = 1; s < edgeSegments; s += 1) {
        points.push(quadraticBezierPoint(shoulder.out, bend, nextShoulder.in, s / edgeSegments));
      }
    }
  }

  return points;
}

export function moveRingVertex(ring: Ring, index: number, point: Vec2): boolean {
  const vertex = ring.vertices[index];

  if (!vertex) {
    return false;
  }

  vertex.x = point.x;
  vertex.y = point.y;
  return true;
}

export function insertRingVertex(ring: Ring, edgeIndex: number, point: Vec2): boolean {
  const n = ring.vertices.length;

  if (edgeIndex < 0 || edgeIndex >= n) {
    return false;
  }

  ring.vertices.splice(edgeIndex + 1, 0, clonePoint(point));
  const previousEdge = ring.edges[edgeIndex];

  if (previousEdge) {
    previousEdge.bend = null;
  }

  ring.edges.splice(edgeIndex + 1, 0, { bend: null });
  return true;
}

/** Removes a vertex, merging its two edges into one straight edge. */
export function removeRingVertex(ring: Ring, index: number): boolean {
  const n = ring.vertices.length;

  if (index < 0 || index >= n || n <= 1) {
    return false;
  }

  const prevEdgeIndex = (index - 1 + n) % n;
  const survivingEdge = ring.edges[prevEdgeIndex];

  ring.vertices.splice(index, 1);
  ring.edges.splice(index, 1);

  if (survivingEdge) {
    survivingEdge.bend = null;
  }

  return true;
}

export function setCornerRadius(ring: Ring, radius: number): void {
  ring.cornerRadius = Math.max(0, radius);
}

/** Hit-tests the straight chord of each not-yet-bent edge, for grabbing a new bend. */
export function findRingEdgeAt(ring: Ring, point: Vec2, radius: number): number | null {
  const n = ring.vertices.length;
  const radiusSq = radius * radius;
  let best: { index: number; distSq: number } | null = null;

  for (let i = 0; i < n; i += 1) {
    if (ring.edges[i]?.bend) {
      continue;
    }

    const a = ring.vertices[i];
    const b = ring.vertices[(i + 1) % n];

    if (!a || !b) {
      continue;
    }

    const closest = closestPointOnSegment(point, a, b);
    const distSq = distanceSquared(point, closest);

    if (distSq <= radiusSq && (!best || distSq < best.distSq)) {
      best = { index: i, distSq };
    }
  }

  return best?.index ?? null;
}

export function findBendHandleAt(ring: Ring, point: Vec2, radius: number): number | null {
  const radiusSq = radius * radius;
  let best: { index: number; distSq: number } | null = null;

  for (let index = 0; index < ring.edges.length; index += 1) {
    const bend = ring.edges[index]?.bend;

    if (!bend) {
      continue;
    }

    const distSq = distanceSquared(point, bend);

    if (distSq <= radiusSq && (!best || distSq < best.distSq)) {
      best = { index, distSq };
    }
  }

  return best?.index ?? null;
}

export function setEdgeBend(ring: Ring, edgeIndex: number, point: Vec2): boolean {
  const edge = ring.edges[edgeIndex];

  if (!edge) {
    return false;
  }

  edge.bend = clonePoint(point);
  return true;
}

export function moveBendHandle(ring: Ring, edgeIndex: number, point: Vec2): boolean {
  const edge = ring.edges[edgeIndex];

  if (!edge || !edge.bend) {
    return false;
  }

  edge.bend.x = point.x;
  edge.bend.y = point.y;
  return true;
}

export function clearEdgeBend(ring: Ring, edgeIndex: number): boolean {
  const edge = ring.edges[edgeIndex];

  if (!edge || edge.bend === null) {
    return false;
  }

  edge.bend = null;
  return true;
}
